import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import notification from "../../../../helpers/notification";
import { TspBreadcrumb } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspApiService from "../../api/tspApiService";
import { tspErrorMessage } from "../../hooks/useTspApiResource";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import useTspAddBookDistributionData from "./useTspAddBookDistributionData";
import useTspBookDistributionPlaces from "./useTspBookDistributionPlaces";
import { pickDefaultVisit, stateOf, visitsInState } from "./placeModel";
import SelectStateCityAreaCard from "./components/SelectStateCityAreaCard";
import BookDistributionRecordsCard from "./components/BookDistributionRecordsCard";

// Add Book Distribution (/tsp/book-distribution/new). Distribution is recorded
// per route place: State, City and Area come from that place's recorded city
// visit and are pre-selected — the place given by ?journey=&location= (the
// journey page's route place links), otherwise the most recent visit. Any
// number of entries are staged locally, then the whole session is sent as one
// atomic batch — see POST /api/tsp/journeys/:id/book-distributions/batch.
export default function TspAddBookDistributionPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const fromJourney = params.get("journey") || "";
  const fromLocation = params.get("location") || "";

  const { data: catalog, loading, error, reload } = useTspAddBookDistributionData();
  const places = useTspBookDistributionPlaces();
  const { hasPermission, loading: permissionsLoading } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.BOOK_DISTRIBUTION_MANAGE);
  const [stateName, setStateName] = useState("");
  const [visitId, setVisitId] = useState("");
  const [placeDefaulted, setPlaceDefaulted] = useState(false);
  const [records, setRecords] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Auto-fill State / City / Area once, as soon as the recorded visits arrive.
  useEffect(() => {
    if (placeDefaulted || !places.data) return;
    const initial = pickDefaultVisit(places.data, { journeyId: fromJourney, locationId: fromLocation });
    if (initial) {
      setStateName(stateOf(initial));
      setVisitId(String(initial.id));
    }
    setPlaceDefaulted(true);
  }, [places.data, placeDefaulted, fromJourney, fromLocation]);

  if (loading) return <div className="tsp-page tsp-dashboard-loading">Loading…</div>;
  if (error) return <TspLoadError error={error} onRetry={reload} what="this form" />;

  const visits = places.data || [];
  const visit = visits.find((v) => String(v.id) === visitId) || null;
  const missingPlace = Boolean(fromLocation) && placeDefaulted && !visit && !pickDefaultVisit(visits, { journeyId: fromJourney, locationId: fromLocation });
  const recordVisitLink = `/tsp/city-visits${fromJourney ? `?journey=${fromJourney}` : ""}`;
  const canSubmit = canManage && visit && records.length > 0 && !submitting;
  // The journey this distribution belongs to: Back and the list after submit return to it.
  const journeyId = visit?.journey?.id ? String(visit.journey.id) : fromJourney;
  const goBack = () => navigate(journeyId ? `/tsp/journeys/${journeyId}` : "/tsp/journeys");

  // A new state: its most recent visit is pre-selected, so City and Area follow.
  const handleStateChange = (name) => {
    setStateName(name);
    const newest = name ? visitsInState(visits, name)[0] : null;
    setVisitId(newest ? String(newest.id) : "");
  };

  const handleSubmit = async () => {
    if (!canManage) {
      notification("error", "You do not have permission to record book distribution.");
      return;
    }
    if (!visit) {
      notification("warning", "Select the state and city first.");
      return;
    }
    if (records.length === 0) {
      notification("warning", "Add at least one distribution entry.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await TspApiService.createBookDistributionBatch(String(visit.journey.id), {
        city_visit_id: Number(visit.id),
        entries: records.map((row) => ({
          ...(row.book_set_id ? { book_set_id: row.book_set_id } : { custom_book_name: row.custom_book_name }),
          language: row.language,
          quantity: row.qty,
          distribution_method: row.method,
        })),
      });
      notification("success", `${records.reduce((sum, row) => sum + row.qty, 0)} books recorded.`);
      navigate(`/tsp/book-distribution?journey=${visit.journey.id}`);
    } catch (submitErr) {
      const message = tspErrorMessage(submitErr, "Could not save this batch. Please try again.");
      notification("error", message);
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tsp-page tsp-add-book-distribution-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Add Book Distribution</h1>
          <TspBreadcrumb items={["Dashboard", "Journeys", "Book Distribution", "Add Book Distribution"]} />
        </div>
        <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={goBack}>
          <ArrowLeft size={14} aria-hidden="true" /> Back
        </button>
      </div>

      {!permissionsLoading && !canManage && <p className="tsp-preview-note tsp-preview-note-warn">You do not have permission to record book distribution.</p>}

      {places.error ? (
        <TspLoadError error={places.error} onRetry={places.reload} what="the recorded city visits" />
      ) : (
        <SelectStateCityAreaCard
          visits={visits}
          stateName={stateName}
          onStateChange={handleStateChange}
          visit={visit}
          onVisitChange={setVisitId}
          missingPlace={missingPlace}
          recordVisitLink={recordVisitLink}
          disabled={places.loading || submitting}
        />
      )}

      <BookDistributionRecordsCard records={records} setRecords={setRecords} bookOptions={catalog || []} disabled={submitting} />

      {submitError && (
        <div className="tsp-form-error" role="alert">
          {submitError}
        </div>
      )}
      <div className="tsp-bd-submit-row">
        <button type="button" className="tsp-btn tsp-btn-success tsp-btn-pill" onClick={handleSubmit} disabled={!canSubmit || permissionsLoading}>
          {submitting ? "Submitting…" : "Submit Book Distribution"}
        </button>
      </div>

      <ToastContainer />
    </div>
  );
}
